import {
  BayLevelEnum,
  ForeAftEnum,
  IBay40sPairLocation,
  IBayLevelData,
  detectBay40sLocation,
} from "open-vessel-definition";
import { IBayPattern, can40sBeDefinedInBay } from "@tedivo/tedivo-bay-grid-pure";
import { IValidationInput, IValidationOutput, IValidator } from "../types";

import { createDictionary } from "@tedivo/tedivo-pure-helpers";

const name = "bay40sLocationValidator";
const okTranslationKey = "validators:bays.bay40sLocation.ok";
const errorTranslationKey = "validators:bays.bay40sLocation.error";

/** Where the convention a pair was judged against came from */
export type TBay40sLocationSource = "shipData" | "detected";

/** How the target Bay of a misplaced pair was chosen */
export type TBay40sTargetDecidedBy =
  /** The vessel's convention, `shipData.bay40sLocation` or the detected majority */
  | TBay40sLocationSource
  /** No vessel convention: the half of the pair holding more 40'+ data wins */
  | "pair"
  /** No vessel convention and both halves hold the same amount: AFT, as the STAF import does */
  | "default";

/** A pair of Bays whose 40'+ data sits, partly or wholly, in the wrong half */
export interface IBay40sMisplacedPair {
  level: BayLevelEnum;
  fwd: IBayPattern;
  aft: IBayPattern;
  /** The half of the pair holding 40'+ data it should not */
  from: ForeAftEnum;
  /** The half the 40'+ data belongs in */
  to: ForeAftEnum;
  fromIsoBay: IBayPattern;
  toIsoBay: IBayPattern;
  /** Both halves hold 40'+ data (the case that breaks the CGs) */
  split: boolean;
  decidedBy: TBay40sTargetDecidedBy;
}

export interface IBay40sLocationOutput {
  /** The convention the file was judged against. `undefined` when none could be established */
  bay40sLocation: ForeAftEnum | undefined;
  bay40sLocationSource: TBay40sLocationSource | undefined;
  misplaced: IBay40sMisplacedPair[];
}

/**
 * Checks that every pair of Bays keeps its 40'+ definitions in the half that
 * `shipData.bay40sLocation` names (or, when the file states none, the half most of
 * the file uses). The same rule the Bay editor applies, see `can40sBeDefinedInBay`,
 * including the trailing-pair exception.
 *
 * Without any convention at all, only a *split* pair (40'+ data in both halves) is
 * reported, because it cannot be drawn: the target is then the half holding more
 * 40'+ data, and AFT when both hold the same.
 *
 * Unpaired Bays with 40s are not this validator's business; see
 * `allBaysWith40sArePairedValidator`.
 */
export const bay40sLocationValidator: IValidator<IBay40sLocationOutput> = {
  name,
  okTranslationKey,
  errorTranslationKey,
  fn: bay40sLocationValidatorFn,
  severity: "error",
  stage: "data",
};

export function bay40sLocationValidatorFn({
  data: { json },
}: IValidationInput): IValidationOutput<IBay40sLocationOutput> {
  const baysData = json.baysData || [];
  const detected = detectBay40sLocation(baysData);

  const fromShipData = json.shipData?.bay40sLocation;
  const bay40sLocation = fromShipData ?? detected.bay40sLocation;
  const bay40sLocationSource: TBay40sLocationSource | undefined =
    fromShipData !== undefined ? "shipData" : bay40sLocation !== undefined ? "detected" : undefined;

  const baysDict = createDictionary(baysData, (bl) => `${bl.isoBay}-${bl.level}`);

  const misplaced: IBay40sMisplacedPair[] = [];

  detected.pairs.forEach((pair) => {
    const decision = decideTarget(pair, bay40sLocation, bay40sLocationSource, baysDict, baysData);
    if (decision === undefined) return;

    const { to, decidedBy } = decision;
    const from = opposite(to);

    misplaced.push({
      level: pair.level,
      fwd: pair.fwd as IBayPattern,
      aft: pair.aft as IBayPattern,
      from,
      to,
      fromIsoBay: isoBayOfSide(pair, from),
      toIsoBay: isoBayOfSide(pair, to),
      split: pair.split,
      decidedBy,
    });
  });

  const isValid = misplaced.length === 0;

  return {
    name,
    isValid,
    messageTranslationKey: isValid ? okTranslationKey : errorTranslationKey,
    result: { bay40sLocation, bay40sLocationSource, misplaced },
  };
}

function decideTarget(
  pair: IBay40sPairLocation,
  bay40sLocation: ForeAftEnum | undefined,
  source: TBay40sLocationSource | undefined,
  baysDict: Record<string, IBayLevelData>,
  baysData: IBayLevelData[],
): { to: ForeAftEnum; decidedBy: TBay40sTargetDecidedBy } | undefined {
  if (pair.fwdCount === 0 && pair.aftCount === 0) return undefined;

  if (bay40sLocation === undefined || source === undefined) {
    // No convention: only a split pair is a problem, and it decides for itself
    if (!pair.split) return undefined;

    return pair.location !== undefined
      ? { to: pair.location, decidedBy: "pair" }
      : { to: ForeAftEnum.AFT, decidedBy: "default" };
  }

  const wrongSide = opposite(bay40sLocation);
  const countOnWrongSide = wrongSide === ForeAftEnum.FWD ? pair.fwdCount : pair.aftCount;
  if (countOnWrongSide === 0) return undefined;

  const wrongIsoBay = isoBayOfSide(pair, wrongSide);
  const wrongBl = baysDict[`${wrongIsoBay}-${pair.level}`];

  // Same rule as the Bay editor, so the trailing pair keeps its exception
  const allowance = can40sBeDefinedInBay(
    wrongIsoBay,
    pair.level,
    wrongBl?.pairedBay,
    bay40sLocation,
    baysData,
  );

  if (allowance.allowed) return undefined;

  return { to: bay40sLocation, decidedBy: source };
}

function opposite(side: ForeAftEnum): ForeAftEnum {
  return side === ForeAftEnum.FWD ? ForeAftEnum.AFT : ForeAftEnum.FWD;
}

function isoBayOfSide(pair: IBay40sPairLocation, side: ForeAftEnum): IBayPattern {
  return (side === ForeAftEnum.FWD ? pair.fwd : pair.aft) as IBayPattern;
}
